import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, ShieldCheck, KeyRound, Smartphone, Bell, Check, PowerOff } from "lucide-react";
import { apiGet, protectedPost } from "@/lib/api";
import { toast } from "sonner";
import { useWorkspace } from "@/context/WorkspaceContext";
import { cn } from "@/lib/utils";

function useClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  return now;
}

export function TopBar() {
  const now = useClock();
  const [status, setStatus] = useState(null);
  const [open, setOpen] = useState(false);
  const [em, setEm] = useState(false);
  const [health, setHealth] = useState(null);
  const { workspace, setWorkspace, workspaces, current } = useWorkspace();
  const ref = useRef(null);

  useEffect(() => {
    apiGet("/system/status").then(setStatus).catch(() => {});
    apiGet("/system/health").then(setHealth).catch(() => {});
    const load = () => apiGet("/emergency/status").then((s) => setEm(s.shutdown)).catch(() => {});
    load();
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
  }, []);

  const emergency = async () => {
    if (em) {
      await protectedPost("/emergency/restore");
      setEm(false);
      toast.success("App-managed workers restored.");
    } else {
      if (!window.confirm("EMERGENCY: stop all app-managed workers and their current jobs? Remote VPS power is not controlled.")) return;
      await protectedPost("/emergency/shutdown-all");
      setEm(true);
      toast.error("Emergency stop engaged — app-managed workers stopped.");
    }
  };

  useEffect(() => {
    const h = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const aiConfigured = status?.ai_configured;

  return (
    <header
      className="h-12 shrink-0 relative z-50 flex items-center justify-between gap-2 px-3 border-b border-jarvis-cyan/12 bg-[#040911]/95 backdrop-blur-xl"
      data-testid="app-topbar"
    >
      <div className="flex items-center gap-2 min-w-0">
        <span className="font-mono text-sm font-bold tracking-[0.3em] text-jarvis-cyan jv-glow">JARVIS</span>

        {/* Workspace switcher */}
        <div className="relative" ref={ref}>
          <button
            onClick={() => setOpen((o) => !o)}
            data-testid="workspace-switcher"
            className="flex items-center gap-2 rounded-md border border-jarvis-cyan/25 bg-black/30 px-2.5 py-1 font-mono text-[11px] uppercase tracking-wider text-slate-200 hover:border-jarvis-cyan/50 transition-colors"
          >
            <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: current.color }} />
            {current.short}
            <ChevronDown className="w-3 h-3 text-slate-500" />
          </button>
          <AnimatePresence>
            {open && (
              <motion.div
                initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }}
                className="absolute left-0 mt-1.5 w-56 z-[70] rounded-md border border-jarvis-cyan/25 bg-[#0a0f1d] py-1 shadow-[0_8px_32px_rgba(0,0,0,0.6)]"
              >
                {workspaces.map((w) => (
                  <button
                    key={w.id}
                    onClick={() => { setWorkspace(w.id); setOpen(false); }}
                    data-testid={`workspace-opt-${w.id}`}
                    className={cn(
                      "w-full flex items-center gap-2.5 px-3 py-2 font-mono text-[11px] uppercase tracking-wider transition-colors",
                      workspace === w.id ? "text-jarvis-cyan bg-jarvis-cyan/10" : "text-slate-400 hover:bg-white/[0.04]"
                    )}
                  >
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: w.color }} />
                    <span className="flex-1 text-left">{w.name}</span>
                    {workspace === w.id && <Check className="w-3.5 h-3.5" />}
                  </button>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div className="hidden md:flex items-center gap-1.5" data-testid="ai-status-indicator">
          <span className="w-1.5 h-1.5 rounded-full jv-pulse" style={{ backgroundColor: aiConfigured ? "#18c9ff" : "#ffb800", color: aiConfigured ? "#18c9ff" : "#ffb800" }} />
          <span className={cn("font-mono text-[10px] tracking-widest", aiConfigured ? "text-jarvis-green" : "text-jarvis-amber")}>
            {status ? (aiConfigured ? "KEY CONFIGURED" : "NOT CONNECTED") : "…"}
          </span>
        </div>
      </div>

      <div className="flex items-center gap-2 lg:gap-3 shrink-0 font-mono text-[10px] tracking-widest">
        <button
          onClick={emergency}
          data-testid="emergency-btn"
          title="Emergency stop — app-managed workers only"
          className={`flex items-center gap-1.5 rounded-md border px-2.5 py-1 uppercase tracking-wider transition-all ${em ? "border-jarvis-green/50 text-jarvis-green bg-jarvis-green/10" : "border-jarvis-red/50 text-jarvis-red bg-jarvis-red/10 hover:bg-jarvis-red/20 jv-pulse"}`}
        >
          <PowerOff className="w-3.5 h-3.5" /> <span className="hidden sm:inline">{em ? "RESTORE" : "EMERGENCY"}</span>
        </button>
        <span data-testid="topbar-sentinel-unconnected" className="hidden xl:flex items-center gap-1.5 text-slate-500"><ShieldCheck className="w-3.5 h-3.5" /> SENTINEL NOT CONNECTED</span>
        <span data-testid="topbar-vault-status" className="hidden xl:flex items-center gap-1.5 text-slate-500"><KeyRound className="w-3.5 h-3.5" /> VAULT {(health?.vault || "—").toUpperCase()}</span>
        <span data-testid="topbar-devices-unconnected" className="hidden lg:flex items-center gap-1.5 text-slate-500"><Smartphone className="w-3.5 h-3.5" /> —</span>
        <span data-testid="topbar-alerts-unconnected" className="relative flex items-center text-slate-500" title="Security alert monitoring is not connected"><Bell className="w-4 h-4" /></span>
        <span className="hidden sm:block text-slate-500">{now.toLocaleTimeString("en-GB")}</span>
      </div>
    </header>
  );
}
